import { forwardRef } from 'react';

/**
 * SophiaLogo — proprietary brand mark. Two renderings:
 *
 *   <SophiaLogo />                   default filled badge (gold on dark)
 *   <SophiaLogo variant="mono" />    inverse (cream on gold / for chips)
 *   <SophiaLogo variant="glyph" />   the bare serif S — no container
 *
 * Replaces the ✨ emoji that was used as a stand-in everywhere. Sized
 * via CSS — accepts any parent width/height. Rendered inline so it
 * adopts the parent's font color when variant="glyph".
 */

interface LogoProps {
  variant?: 'badge' | 'mono' | 'glyph';
  className?: string;
  size?: number;
  'aria-label'?: string;
}

export const SophiaLogo = forwardRef<SVGSVGElement, LogoProps>(function SophiaLogo(
  { variant = 'badge', className, size, 'aria-label': ariaLabel = 'Sophia' },
  ref,
) {
  const dim = size ? { width: size, height: size } : undefined;

  if (variant === 'glyph') {
    // Pure serif S; colored by parent's currentColor. Use this when you
    // want the S inside an already-themed gold/dark container.
    return (
      <svg
        ref={ref}
        viewBox="0 0 32 32"
        role="img"
        aria-label={ariaLabel}
        className={className}
        style={dim}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.8 8.2c-.95-.85-2.32-1.35-4.06-1.35-2.95 0-4.95 1.4-4.95 3.6 0 1.75 1.05 2.8 3.75 3.5l2.05.55c1.55.4 2.1.95 2.1 1.85 0 1.15-1.15 1.85-3 1.85-1.6 0-2.85-.5-4.2-1.65l-.95 1.5c1.45 1.3 3.15 2 5.1 2 3.2 0 5.2-1.4 5.2-3.75 0-1.8-1.05-2.85-3.75-3.55l-2.1-.55c-1.55-.4-2.05-.9-2.05-1.7 0-1.1 1.1-1.8 2.85-1.8 1.35 0 2.5.45 3.5 1.35l.55-1.85Z"
          fill="currentColor"
        />
      </svg>
    );
  }

  if (variant === 'mono') {
    // Cream-filled rounded square with dark serif S inside. Use inside
    // a gold-themed surface (footer, CTA cards).
    return (
      <svg
        ref={ref}
        viewBox="0 0 32 32"
        role="img"
        aria-label={ariaLabel}
        className={className}
        style={dim}
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1" y="1" width="30" height="30" rx="6" fill="#f4ebd7" />
        <path
          d="M19.8 8.2c-.95-.85-2.32-1.35-4.06-1.35-2.95 0-4.95 1.4-4.95 3.6 0 1.75 1.05 2.8 3.75 3.5l2.05.55c1.55.4 2.1.95 2.1 1.85 0 1.15-1.15 1.85-3 1.85-1.6 0-2.85-.5-4.2-1.65l-.95 1.5c1.45 1.3 3.15 2 5.1 2 3.2 0 5.2-1.4 5.2-3.75 0-1.8-1.05-2.85-3.75-3.55l-2.1-.55c-1.55-.4-2.05-.9-2.05-1.7 0-1.1 1.1-1.8 2.85-1.8 1.35 0 2.5.45 3.5 1.35l.55-1.85Z"
          fill="#0a0a0a"
        />
      </svg>
    );
  }

  // Default badge: gold gradient square with cream serif S + subtle luxe
  // accent ring. Designed to sit comfortably in 28-48px range.
  return (
    <svg
      ref={ref}
      viewBox="0 0 32 32"
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={dim}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="sophiaGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f0d499" />
          <stop offset="55%" stopColor="#c9a96a" />
          <stop offset="100%" stopColor="#a5823b" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="30" height="30" rx="7" fill="url(#sophiaGold)" />
      <rect x="2.5" y="2.5" width="27" height="27" rx="5.5" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.5" />
      <path
        d="M19.8 8.2c-.95-.85-2.32-1.35-4.06-1.35-2.95 0-4.95 1.4-4.95 3.6 0 1.75 1.05 2.8 3.75 3.5l2.05.55c1.55.4 2.1.95 2.1 1.85 0 1.15-1.15 1.85-3 1.85-1.6 0-2.85-.5-4.2-1.65l-.95 1.5c1.45 1.3 3.15 2 5.1 2 3.2 0 5.2-1.4 5.2-3.75 0-1.8-1.05-2.85-3.75-3.55l-2.1-.55c-1.55-.4-2.05-.9-2.05-1.7 0-1.1 1.1-1.8 2.85-1.8 1.35 0 2.5.45 3.5 1.35l.55-1.85Z"
        fill="#1a0f06"
      />
    </svg>
  );
});
